import { useEffect, useState } from "react";
import { ArrowDownCircle, Copy, Eye, EyeOff, ImagePlus, PlusCircle, Send, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import KycRequiredDialog from "./KycRequiredDialog";
import { isKycComplete } from "../lib/kyc";
import { VerifiedName } from "./VerifiedBadge";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

const MANUAL_REVIEW_PLATFORM_TYPES = new Set(["chime", "venmo", "paypal", "cashapp", "ecashapp", "xxpay", "xxpayus"]);
const WITHDRAWAL_PLATFORM_TYPES = new Set(["chime", "venmo", "cashapp", "ecashapp"]);
const AUTO_PAYOUT_CASHOUT_TYPES = new Set(["chime", "cashapp", "ecashapp"]);
const WITHDRAWAL_METHOD_KEYS = ["cashout", "withdraw", "withdrawal", "payout"];
const FAVORITE_HOSTS = ["DRACO", "SOPHIA", "AURUM"];
const HOST_TIP_AMOUNTS = [5, 10, 15, 20];
const MIN_WITHDRAW_DOLLARS = 15;
const AUTO_PAYOUT_MIN_DOLLARS = 30;
const AUTO_PAYOUT_LIMIT_DOLLARS = 200;
const MAX_DAILY_WITHDRAW_DOLLARS = 1500;
const MIN_TRANSFER_DOLLARS = 5;
const MAX_TRANSFER_DOLLARS = 50;
const PAYMENT_PLATFORM_REFRESH_MS = 30000;
const CHIME_AMOUNT_PRESETS = [20, 25, 30, 31, 40, 50, 60, 100, 125, 130, 150, 200, 300, 400, 500];
const PLAYHUB_WALLET_AMOUNT_PRESETS = [9.99, 14.99, 17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99, 59.99, 99.99, 124.99, 129.99, 149.99, 199.99, 300, 400, 500];
const PLAYHUB_CARD_AMOUNT_PRESETS = [9.99, 10.99, 11.99, 12.99, 13.99, 14.99, 17.99, 19.99, 22.99, 24.99, 29.99, 30.99, 32.99, 39.99, 49.99, 54.99, 59.99, 99.99, 109.99, 124.99, 129.99, 149.99, 199.99];
const XXPAY_APPLE_GOOGLE_AMOUNT_PRESETS = [9.99, 14.99, 17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99, 59.99, 99.99, 124.99, 129.99, 149.99, 199.99];
const LIGHT_INPUT_ON_DARK_CLASS = "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 placeholder:text-slate-500 shadow-inner [caret-color:#0f172a] [color-scheme:light] focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/25";
const LIGHT_INPUT_ON_DARK_EMERALD_CLASS = "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 placeholder:text-slate-500 shadow-inner [caret-color:#0f172a] [color-scheme:light] focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/25";
const PAYMENT_ICON_ASSETS = {
  card: "/assets/payments/card.png",
  applepay: "/assets/payments/applepay.png",
  googlepay: "/assets/payments/googlepay.png",
  cashapp: "/assets/payments/cashapp.png",
  chime: "/assets/payments/chime.png",
  crypto: "/assets/payments/crypto.jpg",
  paypal: "/assets/payments/paypal.jpg",
  venmo: "/assets/payments/venmo.jpg",
};
const PAYMENT_ICON_TEXT = {
  card: "CARD",
  applepay: "AP",
  googlepay: "GP",
  crypto: "CR",
  paypal: "PP",
  venmo: "V",
  xxpay: "XP",
};

function platformKey(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function checkoutMethodKey(item) {
  const key = platformKey(item?.platform_type || item?.id || item?.label);
  if (key.includes("applepay")) return "applepay";
  if (key.includes("googlepay")) return "googlepay";
  if (key.includes("cashapp")) return "cashapp";
  if (key.includes("chime")) return "chime";
  if (key.includes("card")) return "card";
  return key;
}

function isAllowedCashInProvider(item) {
  if (platformKey(item?.provider) === "keyhub") return false;
  const key = checkoutMethodKey(item);
  if (["applepay", "googlepay"].includes(key)) return item?.provider === "xxpay";
  return true;
}

function amountToCents(value) {
  const dollars = Number(value);
  if (!Number.isFinite(dollars)) return null;
  return Math.round(dollars * 100);
}

function normalizeCheckoutUrl(value) {
  const url = String(value || "").trim();
  if (!url) return "";
  if (url.startsWith("lightning:")) {
    return `https://cash.app/launch/lightning/${url.replace(/^lightning:/i, "")}`;
  }
  return url;
}

function xxpayAmountRule(platform) {
  if (!platform) return null;
  const key = platformKey(platform.id || platform.platform_type || platform.label);
  if (platform?.provider === "playhub" && key === "chime") {
    const allowedCents = new Set(CHIME_AMOUNT_PRESETS.map(amountToCents));
    return {
      presets: CHIME_AMOUNT_PRESETS,
      inputStep: "1",
      message: `Chime only accepts these exact amounts: ${CHIME_AMOUNT_PRESETS.map((value) => `$${value}`).join(", ")}.`,
      isValid: (value) => {
        const cents = amountToCents(value);
        return cents !== null && allowedCents.has(cents);
      },
    };
  }
  if (platform?.provider === "playhub" && key === "card") {
    const allowedCents = new Set(PLAYHUB_CARD_AMOUNT_PRESETS.map(amountToCents));
    return {
      presets: PLAYHUB_CARD_AMOUNT_PRESETS,
      inputStep: "0.01",
      message: `${publicPaymentLabel(platform.label, "Card")} only accepts these exact amounts: ${PLAYHUB_CARD_AMOUNT_PRESETS.map((value) => `$${value}`).join(", ")}.`,
      isValid: (value) => {
        const cents = amountToCents(value);
        return cents !== null && allowedCents.has(cents);
      },
    };
  }
  if (platform?.provider === "playhub" && key !== "cashapplightning") {
    const allowedCents = new Set(PLAYHUB_WALLET_AMOUNT_PRESETS.map(amountToCents));
    return {
      presets: PLAYHUB_WALLET_AMOUNT_PRESETS,
      inputStep: "0.01",
      message: `${publicPaymentLabel(platform.label, "This PlayHub method")} only accepts these exact amounts: ${PLAYHUB_WALLET_AMOUNT_PRESETS.map((value) => `$${value}`).join(", ")}.`,
      isValid: (value) => {
        const cents = amountToCents(value);
        return cents !== null && allowedCents.has(cents);
      },
    };
  }
  if (platform?.provider !== "xxpay") return null;
  if (["ecashapp", "applepay", "googlepay"].includes(key)) {
    const exactCents = ["applepay", "googlepay"].includes(key)
      ? new Set(XXPAY_APPLE_GOOGLE_AMOUNT_PRESETS.map(amountToCents))
      : null;
    const minCents = 999;
    const maxCents = key === "ecashapp" ? 50000 : 19999;
    const presets = exactCents
      ? XXPAY_APPLE_GOOGLE_AMOUNT_PRESETS
      : [9.99, 14.99, 19.99, 24.99, 49.99];
    const minLabel = "$9.99";
    return {
      presets,
      inputStep: "0.01",
      message: exactCents
        ? `${publicPaymentLabel(platform.label, "This XXPay method")} only accepts these exact amounts: ${presets.map((value) => `$${value}`).join(", ")}. Wallet credit rounds up to the next dollar.`
        : `${publicPaymentLabel(platform.label, "This XXPay method")} accepts amounts ending in .99, from ${minLabel} to $${(maxCents / 100).toFixed(2)}. Wallet credit rounds up to the next dollar.`,
      isValid: (value) => {
        const cents = amountToCents(value);
        if (cents === null) return false;
        if (exactCents) return exactCents.has(cents);
        return cents >= minCents && cents <= maxCents && cents % 100 === 99;
      },
    };
  }
  if (key === "chime") {
    const allowedCents = new Set(CHIME_AMOUNT_PRESETS.map(amountToCents));
    return {
      presets: CHIME_AMOUNT_PRESETS,
      inputStep: "1",
      message: `XXPay Chime only accepts these exact amounts: ${CHIME_AMOUNT_PRESETS.map((value) => `$${value}`).join(", ")}.`,
      isValid: (value) => {
        const cents = amountToCents(value);
        return cents !== null && allowedCents.has(cents);
      },
    };
  }
  return null;
}

function publicPaymentLabel(value, fallback = "Checkout") {
  return String(value || fallback)
    .replace(/\bkey\s*hub\b/gi, "")
    .replace(/\bkeyhub\b/gi, "")
    .replace(/\s+checkout\b/gi, " checkout")
    .replace(/\s+/g, " ")
    .trim() || fallback;
}

function isWithdrawalLimitMessage(value) {
  return /24-hour wallet withdrawal limit|remaining withdrawal limit|maximum wallet withdrawal/i.test(String(value || ""));
}

function isKycRequiredMessage(value) {
  return /verify your phone|id front|id back|selfie|approved before withdrawing|before withdrawing money|kyc/i.test(String(value || ""));
}

async function copyToClipboard(value, label = "Tag") {
  const text = String(value || "").trim();
  if (!text) {
    toast.error(`No ${label.toLowerCase()} to copy.`);
    return;
  }
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const input = document.createElement("textarea");
      input.value = text;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    toast.success(`${label} copied`);
  } catch {
    toast.error(`Could not copy ${label.toLowerCase()}.`);
  }
}

function paymentIconKey(item, label) {
  const raw = `${item?.platform_type || ""} ${item?.id || ""} ${item?.label || ""} ${label || ""}`;
  const key = platformKey(raw);
  if (key.includes("cashapp")) return "cashapp";
  if (key.includes("chime")) return "chime";
  if (key.includes("applepay")) return "applepay";
  if (key.includes("googlepay")) return "googlepay";
  if (key.includes("paypal")) return "paypal";
  if (key.includes("venmo")) return "venmo";
  if (key.includes("xxpay")) return "xxpay";
  if (key.includes("crypto") || key.includes("nowpayments")) return "crypto";
  if (key.includes("card") || key.includes("credit") || key.includes("debit")) return "card";
  return "card";
}

function isRecommendedPayment(item) {
  return item?.provider === "playhub" && platformKey(item?.id || item?.label) === "cashapplightning";
}

function paymentSortScore(item) {
  if (isRecommendedPayment(item)) return 0;
  if (item?.provider === "playhub" && platformKey(item?.id || item?.label) === "cashapp") return 1;
  if (item?.provider === "playhub") return 2;
  if (item?.configured) return 3;
  if (item?.manual) return 4;
  return 5;
}

function PaymentMethodIcon({ item, label }) {
  const key = paymentIconKey(item, label);
  const image = PAYMENT_ICON_ASSETS[key];
  if (image) {
    return (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white">
        <img src={image} alt="" className="h-full w-full object-cover" />
      </span>
    );
  }
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#2F6FA5]/35 bg-[#2F6FA5]/15 text-[11px] font-black tracking-[0.08em] text-[#B7D7F2]">
      {PAYMENT_ICON_TEXT[key] || "PAY"}
    </span>
  );
}

function manualPlatform(row) {
  const platformType = platformKey(row.type || "manual");
  return {
    id: row.id || row.type,
    platform_type: platformType,
    label: publicPaymentLabel(row.name || row.type || "Manual", "Manual"),
    provider: "manual",
    configured: false,
    manual: true,
    method_id: row.id,
    instructions: row.instructions || "",
    account_label: row.account_label || "",
    account_value: row.account_value || "",
    qr_code_url: row.qr_code_url || "",
    auto_payout_enabled: !!row.auto_payout_enabled,
  };
}

function isCashoutRow(row) {
  if ((row.provider || "manual") !== "manual") return false;
  if (!WITHDRAWAL_PLATFORM_TYPES.has(platformKey(row.type))) return false;
  return true;
}

function methodPurpose(row) {
  const purpose = platformKey(row?.purpose);
  if (["cashin", "cashout", "both"].includes(purpose)) return purpose;
  return "";
}

function allowsCashInRow(row) {
  const purpose = methodPurpose(row);
  return purpose !== "cashout";
}

function isDedicatedCashoutRow(row) {
  if (!isCashoutRow(row)) return false;
  if (["cashout", "both"].includes(methodPurpose(row))) return true;
  const text = `${platformKey(row.name)} ${platformKey(row.instructions)}`;
  return WITHDRAWAL_METHOD_KEYS.some((key) => text.includes(key));
}

function isAutoPayoutCashout(item) {
  return item?.provider === "manual" && !!item?.auto_payout_enabled && AUTO_PAYOUT_CASHOUT_TYPES.has(platformKey(item?.platform_type || item?.id || item?.label));
}

function payoutProofInstruction(item, label = "cashout") {
  const key = platformKey(item?.platform_type || item?.id || item?.label || label);
  if (key.includes("cashapp") || key.includes("ecashapp")) {
    return "Optional: upload your Cash App QR code or $Cashtag screenshot so admin can verify the payout destination.";
  }
  if (key.includes("venmo")) {
    return "Optional: upload your Venmo QR code or @Venmo tag screenshot so admin can verify the payout destination.";
  }
  if (key.includes("chime")) {
    return "Optional: upload your Chime QR code or $ChimeSign screenshot so admin can verify the payout destination.";
  }
  return `Optional: upload your ${label} QR code or payout tag screenshot so admin can verify the payout destination.`;
}

function cashoutRequestLabel(item, label = "Cashout") {
  const key = platformKey(item?.platform_type || item?.id || item?.label || label);
  if (key.includes("cashapp") || key.includes("ecashapp")) return "Cash App request tag";
  if (key.includes("venmo")) return "Venmo request QR / tag";
  if (key.includes("chime")) return "Chime request QR / tag";
  return `${label} request QR / tag`;
}

export default function WalletHeroCard({ user, className = "", onBalanceChange }) {
  const { isDark } = useTheme();
  const { refreshUser } = useAuth();
  const [showBalance, setShowBalance] = useState(true);
  const [open, setOpen] = useState(null);
  const [kycPromptAction, setKycPromptAction] = useState("");
  const [kycPromptUser, setKycPromptUser] = useState(null);
  const [checkingAction, setCheckingAction] = useState("");
  const coins = user?.coins || 0;
  const freePlay = Number(user?.free_play || 0);
  const usd = (coins / 1000).toFixed(2);
  const freePlayUsd = (freePlay / 1000).toFixed(2);
  const heroClass = isDark
    ? "relative max-w-full overflow-hidden rounded-2xl md:rounded-3xl border-2 border-[#2F6FA5]/35 bg-[#081421] p-5 text-white shadow-sm sm:p-7 md:p-10"
    : "relative max-w-full overflow-hidden rounded-2xl md:rounded-3xl border-2 border-[#2F6FA5]/25 bg-white p-5 text-slate-950 shadow-sm sm:p-7 md:p-10";

  const openWalletAction = async (action) => {
    if (action === "withdraw") {
      setCheckingAction(action);
      try {
        const latestUser = await refreshUser?.({ silent: true });
        const effectiveUser = latestUser || user;
        if (!isKycComplete(effectiveUser)) {
          setKycPromptUser(effectiveUser);
          setKycPromptAction(action);
          return;
        }
      } finally {
        setCheckingAction("");
      }
    }
    setOpen(action);
  };

  return (
    <>
      <div className={`${heroClass} ${className}`} data-testid="wallet-hero">
        <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden md:block opacity-90">
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-3">
            <Reel emoji="7" />
            <Reel emoji="7" />
            <Reel emoji="7" />
          </div>
        </div>

        <div className="relative max-w-xl min-w-0">
          <div className="text-xs uppercase tracking-[0.25em] text-[#2F6FA5] font-semibold mb-2">
            <span className="inline-flex items-center gap-1.5">
              {greeting()},
              <VerifiedName name={user?.username || "player"} user={user} />
            </span>
          </div>

          <div className="mt-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#2F6FA5]/80 font-semibold mb-2">Wallet balance</div>
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              <Wallet className="w-8 h-8 sm:w-9 sm:h-9 text-[#2F6FA5] shrink-0" />
              <div className="min-w-0 break-words font-display font-black text-4xl sm:text-5xl md:text-6xl tabular-nums leading-none gold-text" data-testid="wallet-balance">
                {showBalance ? `$${usd}` : "$ ..."}
              </div>
              <button onClick={() => setShowBalance((s) => !s)} data-testid="toggle-balance"
                className={`p-2 rounded-full border sm:ml-2 shrink-0 ${
                  isDark
                    ? "border-white/15 text-slate-300 hover:bg-white/10"
                    : "border-slate-200 text-slate-600 hover:bg-slate-100"
                }`} title="Show/hide balance">
                {showBalance ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            <div className={`mt-4 inline-flex max-w-full items-center gap-2 rounded-full border px-4 py-2 text-sm ${
              isDark ? "border-[#2F6FA5]/35 bg-[#0B2034] text-slate-300" : "border-[#2F6FA5]/25 bg-[#EAF4FC] text-slate-600"
            }`}>
              <span className={isDark ? "text-slate-300" : "text-slate-500"}>Freeplay</span>
              <span className="font-display font-bold tabular-nums text-[#2F6FA5]" data-testid="freeplay-balance">
                {showBalance ? `$${freePlayUsd}` : "$ ..."}
              </span>
            </div>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <button
              onClick={() => openWalletAction("add")}
              data-testid="wallet-add-money"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#2F6FA5] text-white font-bold hover:bg-[#4E91C8] transition-all"
            >
              <PlusCircle className="w-4 h-4" />
              Add money
            </button>
            <button
              onClick={() => openWalletAction("withdraw")}
              disabled={checkingAction === "withdraw"}
              data-testid="wallet-withdraw-money"
              className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border font-bold transition-all ${
                isDark
                  ? "border-white/15 bg-[#050B14] text-white hover:border-[#2F6FA5]/50 hover:bg-[#0B2034]"
                  : "border-slate-200 bg-white text-slate-800 hover:border-[#2F6FA5]/40 hover:bg-[#EAF4FC]"
              }`}
            >
              <ArrowDownCircle className="w-4 h-4 text-[#2F6FA5]" />
              Withdraw money
            </button>
            <button
              onClick={() => setOpen("transfer")}
              data-testid="wallet-transfer-money"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 font-bold hover:border-emerald-300 hover:bg-emerald-100 transition-all"
            >
              <Send className="w-4 h-4 text-emerald-600" />
              Send to friend
            </button>
          </div>
        </div>
      </div>

      {open === "add" && <MoneyDialog type="load" title="Add money" user={user} onClose={() => setOpen(null)} />}
      {open === "withdraw" && (
        <MoneyDialog
          type="redeem"
          title="Withdraw money"
          user={user}
          onClose={() => setOpen(null)}
          onKycRequired={async () => {
            const latestUser = await refreshUser?.({ silent: true });
            setOpen(null);
            setKycPromptUser(latestUser || user);
            setKycPromptAction("withdraw");
          }}
        />
      )}
      {kycPromptAction && (
        <KycRequiredDialog
          context="withdraw money"
          user={kycPromptUser || user}
          onClose={() => {
            setKycPromptAction("");
            setKycPromptUser(null);
          }}
        />
      )}
      {open === "transfer" && (
        <TransferDialog
          user={user}
          onClose={() => setOpen(null)}
          onBalanceChange={onBalanceChange}
        />
      )}
    </>
  );
}

function TransferDialog({ user, onClose, onBalanceChange }) {
  const [recipientUsername, setRecipientUsername] = useState("");
  const [amount, setAmount] = useState(String(MIN_TRANSFER_DOLLARS));
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const balanceDollars = (user?.coins || 0) / 1000;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const dollars = Number(amount);
    if (!recipientUsername.trim()) {
      setErr("Enter your friend's username.");
      return;
    }
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setErr("Enter a valid amount.");
      return;
    }
    if (dollars < MIN_TRANSFER_DOLLARS) {
      setErr(`Minimum transfer is $${MIN_TRANSFER_DOLLARS}.`);
      return;
    }
    if (dollars > MAX_TRANSFER_DOLLARS) {
      setErr(`Maximum transfer is $${MAX_TRANSFER_DOLLARS}.`);
      return;
    }
    if (dollars > balanceDollars) {
      setErr("Transfer amount is higher than your wallet balance.");
      return;
    }
    setLoading(true);
    try {
      const coins = Math.round(dollars * 1000);
      const { data } = await api.post("/me/wallet-transfer", {
        recipient_username: recipientUsername.trim(),
        amount: coins,
        note: note.trim(),
      });
      toast.success(`Sent $${dollars.toFixed(2)} to ${data?.recipient_username || recipientUsername.trim()}`);
      onBalanceChange?.(data);
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] overflow-y-auto overscroll-contain bg-slate-900/35 p-3 py-5 pb-[calc(2rem+env(safe-area-inset-bottom))] backdrop-blur-sm sm:grid sm:place-items-center sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-4 text-slate-950 shadow-2xl sm:p-7"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold leading-tight">Send to friend</h3>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              Transfer wallet money by username. Received transfers can load games, but cannot be cashed out directly.
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-slate-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs leading-relaxed text-emerald-700">
            Min ${MIN_TRANSFER_DOLLARS}, max ${MAX_TRANSFER_DOLLARS} per transfer. Daily limit is $100. Same device, same phone, and Gmail dot variants are blocked.
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-slate-500">Friend username</label>
            <input
              type="text"
              required
              value={recipientUsername}
              onChange={(e) => setRecipientUsername(e.target.value)}
              placeholder="Enter username"
              className={LIGHT_INPUT_ON_DARK_EMERALD_CLASS}
            />
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-slate-500">Amount ($)</label>
            <input
              type="number"
              required
              min={MIN_TRANSFER_DOLLARS}
              max={MAX_TRANSFER_DOLLARS}
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={LIGHT_INPUT_ON_DARK_EMERALD_CLASS}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {[5, 10, 20, 50].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setAmount(String(value))}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-700 hover:bg-slate-50"
              >
                ${value}
              </button>
            ))}
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-slate-500">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={160}
              placeholder="Short note"
              className={LIGHT_INPUT_ON_DARK_EMERALD_CLASS}
            />
          </div>

          {err && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-emerald-600 py-3 font-bold text-white transition hover:bg-emerald-500 disabled:opacity-50"
          >
            {loading ? "Sending..." : "Send wallet transfer"}
          </button>
        </form>
      </div>
    </div>
  );
}

function MoneyDialog({ type, title, user, onClose, onKycRequired }) {
  const [amount, setAmount] = useState(type === "load" ? "" : "10");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckout] = useState({ configured: false, platforms: [], missing: [] });
  const [platform, setPlatform] = useState("");
  const [proofImage, setProofImage] = useState("");
  const [proofName, setProofName] = useState("");
  const [tipHost, setTipHost] = useState("");
  const [tipAmount, setTipAmount] = useState("");
  const [tipManual, setTipManual] = useState(false);
  const balanceDollars = (user?.coins || 0) / 1000;
  const isWithdraw = type === "redeem";
  const selectedPlatform = checkout.platforms?.find((item) => `${item.provider}:${item.id}` === platform);
  const selectedPlatformLabel = publicPaymentLabel(selectedPlatform?.label, isWithdraw ? "Cashout" : "Checkout");
  const canAutoPayout = isWithdraw && isAutoPayoutCashout(selectedPlatform);
  const selectedAmountRule = xxpayAmountRule(selectedPlatform);
  const amountPresets = selectedAmountRule?.presets || (isWithdraw ? [15, 20, 50, 100] : [10, 20, 50, 100]);
  const needsManualProof = !isWithdraw && selectedPlatform?.manual;
  const canUploadProof = isWithdraw || needsManualProof;
  const tipDollars = Number(tipAmount || 0);
  const hasHostTip = isWithdraw && tipHost && Number.isFinite(tipDollars) && tipDollars > 0;
  const payoutAfterTip = hasHostTip ? Math.max(0, Number(amount || 0) - tipDollars) : Number(amount || 0);
  const gameLoadOnlyDollars = Math.max(
    0,
    Number(user?.transfer_locked_coins || 0) + Number(user?.playthrough_locked_coins || 0)
  ) / 1000;
  const withdrawableDollars = Math.max(0, balanceDollars - gameLoadOnlyDollars);
  const withdrawLimitError = isWithdraw && isWithdrawalLimitMessage(err);

  const checkoutAmountFor = (platformItem, currentAmount = amount) => {
    const rule = xxpayAmountRule(platformItem);
    let dollars = Number(currentAmount);
    return { dollars, rule };
  };

  const openConfiguredCheckout = async (platformItem, currentAmount = amount) => {
    const id = `${platformItem.provider}:${platformItem.id}`;
    setPlatform(id);
    setErr("");

    if (!platformItem?.configured) {
      setErr("Payment API is not configured for this method yet.");
      return;
    }

    const { dollars, rule } = checkoutAmountFor(platformItem, currentAmount);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setErr("Enter a valid amount.");
      return;
    }
    if (rule && !rule.isValid(dollars)) {
      setErr(rule.message);
      return;
    }

    setAmount(String(dollars));
    setLoading(true);
    const selectedMethodKey = checkoutMethodKey(platformItem);
    const canUseProviderFailover = !["applepay", "googlepay"].includes(selectedMethodKey);
    const fallbackCandidates = canUseProviderFailover ? checkout.platforms
      .filter((item) =>
        item?.configured &&
        !item?.manual &&
        `${item.provider}:${item.id}` !== id &&
        checkoutMethodKey(item) === selectedMethodKey
      )
      .sort((a, b) => paymentSortScore(a) - paymentSortScore(b)) : [];
    const candidates = [platformItem, ...fallbackCandidates];
    let lastError = null;
    try {
      for (const candidate of candidates) {
        const label = publicPaymentLabel(candidate.label, "Online");
        const rule = xxpayAmountRule(candidate);
        if (rule && !rule.isValid(dollars)) continue;
        try {
          const coins = Math.round(dollars * 1000);
          const { data } = await api.post(`/payments/${candidate.provider}`, {
            platform: candidate.id,
            amount: coins,
            note: `${label} checkout: $${dollars.toFixed(2)}${note ? ` - ${note}` : ""}`,
          });
          if (data?.hold) {
            toast.info(data.message || `${label} request is pending provider availability.`);
            onClose();
            return;
          }
          const checkoutUrl = normalizeCheckoutUrl(data?.pay_url || data?.payment_url || data?.paymentUrl || data?.payAddress);
          if (!checkoutUrl) throw new Error("Checkout URL missing from payment provider response");
          setPlatform(`${candidate.provider}:${candidate.id}`);
          toast.success(`Opening ${label}`);
          window.__sophiaCheckoutRedirecting = true;
          window.location.assign(checkoutUrl);
          return;
        } catch (e) {
          lastError = e;
        }
      }
      throw lastError || new Error("Checkout unavailable for this method");
    } catch (e) {
      setErr(formatApiError(e));
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadPlatforms = () => {
      const timestamp = Date.now();
      const requests = isWithdraw
        ? [api.get(`/payment-methods?t=${timestamp}`)]
        : [
            api.get(`/payments/playhub/platforms?t=${timestamp}`),
            api.get(`/payments/nowpayments/platforms?t=${timestamp}`),
            api.get(`/payments/xxpay/platforms?t=${timestamp}`),
            api.get(`/payment-methods?t=${timestamp}`),
          ];
      Promise.allSettled(requests).then((results) => {
        if (cancelled) return;
        if (isWithdraw) {
          const manualRows = results[0]?.status === "fulfilled" ? (results[0].value?.data || []) : [];
          const chimeRows = manualRows
            .filter((row) => (row.provider || "manual") === "manual")
            .map((row) => ({ ...row, platform_type: platformKey(row.type || "manual") }))
            .filter(isCashoutRow);
          const namedCashoutRows = chimeRows.filter(isDedicatedCashoutRow);
          const payoutPlatforms = (namedCashoutRows.length ? namedCashoutRows : chimeRows)
            .map(manualPlatform)
            .sort((a, b) =>
              Number(isAutoPayoutCashout(a)) - Number(isAutoPayoutCashout(b)) ||
              publicPaymentLabel(a.label, "").localeCompare(publicPaymentLabel(b.label, ""))
            );
          setCheckout({ configured: false, platforms: payoutPlatforms, missing: [] });
          const first = payoutPlatforms[0];
          setPlatform((current) => {
            if (current && payoutPlatforms.some((item) => `${item.provider}:${item.id}` === current)) return current;
            return first ? `${first.provider}:${first.id}` : "";
          });
          return;
        }
        const checkoutResults = results.slice(0, -1);
        const checkoutPayloads = checkoutResults
          .filter((item) => item.status === "fulfilled")
          .map((item) => item.value?.data)
          .filter(Boolean);
        const manualResult = results[results.length - 1];
        const manualRows = manualResult?.status === "fulfilled" ? (manualResult.value?.data || []) : [];
        const platforms = checkoutPayloads.flatMap((payload) =>
          (payload.platforms || []).map((item) => ({
            ...item,
            configured: !!payload.configured,
            missing: payload.missing || [],
          }))
        );
        const configured = checkoutPayloads.some((payload) => payload.configured);
        const configuredProviders = new Set(
          checkoutPayloads
            .filter((payload) => payload.configured)
            .flatMap((payload) => (payload.platforms || []).map((item) => item.provider))
        );
        const visiblePlatforms = (configured ? platforms.filter((item) => item.configured) : platforms)
          .filter(isAllowedCashInProvider);
        const manualPlatforms = manualRows
          .filter((row) => (row.provider || "manual") === "manual")
          .map((row) => ({ ...row, platform_type: platformKey(row.type || "manual") }))
          .filter((row) => MANUAL_REVIEW_PLATFORM_TYPES.has(row.platform_type))
          .filter((row) => !(configuredProviders.has("xxpay") && ["xxpay", "xxpayus"].includes(row.platform_type)))
          .filter(allowsCashInRow)
          .map(manualPlatform);
        const allPlatforms = [...visiblePlatforms, ...manualPlatforms]
          .filter((item, index, list) =>
            index === list.findIndex((other) => other.provider === item.provider && other.id === item.id)
          )
          .sort((a, b) => paymentSortScore(a) - paymentSortScore(b));
        const missing = checkoutPayloads.flatMap((payload) => payload.missing || []);
        setCheckout({ configured, platforms: allPlatforms, missing });
        const recommended = allPlatforms.find(isRecommendedPayment);
        const first = recommended || allPlatforms.find((item) => item.configured) || allPlatforms[0];
        setPlatform((current) => {
          if (recommended && current === "playhub:cashapp") return `${recommended.provider}:${recommended.id}`;
          if (current && allPlatforms.some((item) => `${item.provider}:${item.id}` === current)) return current;
          return first ? `${first.provider}:${first.id}` : "";
        });
      }).catch(() => {});
    };
    loadPlatforms();
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "hidden") loadPlatforms();
    }, PAYMENT_PLATFORM_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isWithdraw]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const dollars = Number(amount);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setErr("Enter a valid amount.");
      return;
    }
    if (isWithdraw && dollars > withdrawableDollars) {
      setErr(`Only $${withdrawableDollars.toFixed(2)} is withdrawable. Wallet deposits and transfers must be loaded into a game before cashout.`);
      return;
    }
    if (isWithdraw && dollars < MIN_WITHDRAW_DOLLARS) {
      setErr(`Minimum withdrawal is $${MIN_WITHDRAW_DOLLARS}.`);
      return;
    }
    if (isWithdraw && dollars > MAX_DAILY_WITHDRAW_DOLLARS) {
      setErr(`Maximum wallet withdrawal is $${MAX_DAILY_WITHDRAW_DOLLARS} per 24 hours.`);
      return;
    }
    if (isWithdraw && (!selectedPlatform || !WITHDRAWAL_PLATFORM_TYPES.has(selectedPlatform.platform_type))) {
      setErr("Choose Chime, Cash App, or Venmo cashout.");
      return;
    }
    if (isWithdraw && !note.trim()) {
      setErr(`Enter your ${selectedPlatformLabel} payout tag, phone, or email.`);
      return;
    }
    if (isWithdraw && tipHost && (!Number.isFinite(tipDollars) || tipDollars <= 0)) {
      setErr("Choose a tip amount for your favorite host.");
      return;
    }
    if (isWithdraw && tipDollars > 0 && !tipHost) {
      setErr("Choose your favorite host for the tip.");
      return;
    }
    if (isWithdraw && tipDollars >= dollars) {
      setErr("Host tip must be less than your withdrawal amount.");
      return;
    }
    if (needsManualProof && !proofImage) {
      setErr(`Upload your ${selectedPlatformLabel || "payment"} screenshot first.`);
      return;
    }
    if (!isWithdraw && selectedPlatform && !selectedPlatform.manual && !selectedPlatform.configured) {
      setErr("Payment API is not configured for this method yet.");
      return;
    }
    if (!isWithdraw && selectedAmountRule && !selectedAmountRule.isValid(dollars)) {
      setErr(selectedAmountRule.message);
      return;
    }
    setLoading(true);
    try {
      const coins = Math.round(dollars * 1000);
      const action = isWithdraw ? "Withdrawal" : "Add money";
      if (!isWithdraw && selectedPlatform?.configured) {
        await openConfiguredCheckout(selectedPlatform, dollars);
        return;
      }
      await api.post("/me/requests", {
        type,
        amount: coins,
        game_id: null,
        payment_provider: isWithdraw ? "manual" : selectedPlatform?.manual ? "manual" : selectedPlatform?.provider || "",
        payment_platform: isWithdraw ? selectedPlatform?.platform_type || "" : selectedPlatform?.manual ? selectedPlatform?.platform_type || selectedPlatform?.id || "" : selectedPlatform?.id || "",
        payment_method_id: selectedPlatform?.method_id || "",
        proof_image_url: proofImage || "",
        proof_image_name: proofName || "",
        note: `${action}${selectedPlatform?.manual ? ` via ${selectedPlatformLabel}` : ""}: $${dollars.toFixed(2)}${hasHostTip ? ` - Favorite host tip: ${tipHost} $${tipDollars.toFixed(2)} - Estimated payout after tip: $${payoutAfterTip.toFixed(2)}` : ""}${note ? ` - ${isWithdraw ? `${selectedPlatformLabel} payout: ${note}` : note}` : ""}`,
      });
      toast.success(`${title} request submitted`);
      onClose();
    } catch (e) {
      const message = formatApiError(e);
      if (isWithdraw && isKycRequiredMessage(message)) {
        setErr("Verify your phone and ID/selfie before withdrawing money.");
        await onKycRequired?.();
        return;
      }
      setErr(message);
    } finally {
      setLoading(false);
    }
  };

  const handleProof = (file) => {
    setErr("");
    setProofImage("");
    setProofName("");
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setErr("Please upload an image screenshot.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setErr("Screenshot is too large. Please upload an image under 4 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setProofImage(String(reader.result || ""));
      setProofName(file.name);
    };
    reader.onerror = () => setErr("Could not read screenshot. Try another image.");
    reader.readAsDataURL(file);
  };

  return (
    <div
      className="fixed inset-0 z-[80] overflow-y-auto overscroll-contain bg-slate-900/35 p-3 py-5 pb-[calc(8rem+env(safe-area-inset-bottom))] backdrop-blur-sm sm:grid sm:place-items-center sm:p-4 sm:pb-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mx-auto w-full max-w-[420px] rounded-2xl border border-slate-200 bg-white p-3 pb-2 text-slate-950 shadow-2xl sm:max-h-[calc(100dvh-1.5rem)] sm:overflow-y-auto sm:p-5"
      >
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-bold leading-tight">{title}</h3>
            <p className="mt-1 text-[11px] leading-snug text-slate-500">
              {isWithdraw ? `Choose manual cashout for admin review, or auto cashout for $${AUTO_PAYOUT_MIN_DOLLARS} to under $${AUTO_PAYOUT_LIMIT_DOLLARS} when available.` : "Choose a payment method and continue checkout."}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3 pb-1">
          {isWithdraw && (
            <div>
              <label className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-slate-500">Cashout method</label>
              {checkout.platforms?.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {checkout.platforms.map((item) => {
                    const id = `${item.provider}:${item.id}`;
                    const label = publicPaymentLabel(item.label, "Cashout");
                    const autoPayout = isAutoPayoutCashout(item);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setPlatform(id)}
                        className={`flex min-h-[62px] items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left text-xs font-bold transition-all ${
                          platform === id
                            ? "border-[#2F6FA5] bg-[#EAF4FC] text-[#2F6FA5]"
                            : "border-slate-200 bg-white text-slate-700 hover:border-[#2F6FA5]/35 hover:bg-slate-50"
                        }`}
                      >
                        <PaymentMethodIcon item={item} label={label} />
                        <span className="min-w-0">
                          <span className="block truncate leading-tight">{label}</span>
                          <span className="mt-0.5 block text-[9px] uppercase tracking-[0.14em] text-slate-400">
                            {autoPayout ? `Auto $${AUTO_PAYOUT_MIN_DOLLARS}-$${AUTO_PAYOUT_LIMIT_DOLLARS - 1}` : "Manual cashout"}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                  Cashout is not active yet. Ask admin to enable Chime, Cash App, or Venmo.
                </div>
              )}
            </div>
          )}

          {isWithdraw && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs leading-snug text-emerald-800">
              <div className="font-bold text-emerald-900">Auto payout</div>
              <p className="mt-0.5">
                Only methods marked Auto are paid automatically from ${AUTO_PAYOUT_MIN_DOLLARS} to under ${AUTO_PAYOUT_LIMIT_DOLLARS}. Manual Chime/Cash App/Venmo requests stay pending for admin review.
              </p>
            </div>
          )}

          {isWithdraw && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-snug text-amber-800">
              <div className="font-bold text-amber-900">24-hour withdrawal limit</div>
              <p className="mt-0.5">
                Each player can withdraw up to ${MAX_DAILY_WITHDRAW_DOLLARS} from wallet in 24 hours. This keeps payouts safer and prevents one account from taking too much out at once.
              </p>
              <p className="mt-0.5 text-[11px] font-semibold text-amber-700">
                Pending withdrawals also count until admin approves or rejects them.
              </p>
            </div>
          )}

          {isWithdraw && selectedPlatform && (selectedPlatform.account_value || selectedPlatform.qr_code_url) && (
            <div className="rounded-xl border border-[#2F6FA5]/25 bg-[#EAF4FC] p-3 text-xs leading-snug text-slate-700">
              <div className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-[#2F6FA5]">
                {cashoutRequestLabel(selectedPlatform, selectedPlatformLabel)}
              </div>
              {selectedPlatform.account_value && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-[#2F6FA5]/15 bg-white/80 px-3 py-2">
                  <span className="min-w-0">
                    <span className="font-semibold">{selectedPlatform.account_label || selectedPlatformLabel || "Request tag"}:</span>{" "}
                    <span className="break-all font-mono font-black text-slate-900">{selectedPlatform.account_value}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(selectedPlatform.account_value, `${selectedPlatformLabel} tag`)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#2F6FA5]/25 bg-white px-3 py-1.5 text-[11px] font-black text-[#2F6FA5] hover:bg-[#F2F8FD]"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </button>
                </div>
              )}
              {selectedPlatform.qr_code_url && (
                <div className="mt-3 rounded-xl border border-[#2F6FA5]/15 bg-white/80 p-3">
                  <img
                    src={selectedPlatform.qr_code_url}
                    alt={`${selectedPlatformLabel} request QR code`}
                    className="mx-auto max-h-52 w-full rounded-lg bg-white object-contain p-2"
                  />
                  <p className="mt-2 text-center text-[11px] font-semibold text-slate-500">
                    Scan or copy this {selectedPlatformLabel} tag for the request.
                  </p>
                </div>
              )}
            </div>
          )}

          {!isWithdraw && (
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Payment method</label>
              {checkout.platforms?.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 min-[420px]:grid-cols-2">
                  {checkout.platforms.map((item) => {
                    const id = `${item.provider}:${item.id}`;
                    const label = publicPaymentLabel(item.label, "Checkout");
                    const recommended = isRecommendedPayment(item);
                    return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setPlatform(id)}
                      className={`relative flex min-h-[82px] items-center gap-3 rounded-xl border px-3 py-2 text-left text-xs font-bold transition-all min-[360px]:text-sm ${
                        platform === id
                          ? "border-[#2F6FA5] bg-[#EAF4FC] text-[#2F6FA5]"
                          : "border-slate-200 bg-white text-slate-700 hover:border-[#2F6FA5]/35 hover:bg-slate-50"
                      }`}
                    >
                      {recommended && (
                        <span className="absolute right-2 top-2 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] text-emerald-700">
                          Recommended
                        </span>
                      )}
                      <PaymentMethodIcon item={item} label={label} />
                      <span className="min-w-0 pr-1">
                        <span className="block truncate leading-tight">{label}</span>
                        <span className="mt-1 block text-[10px] uppercase tracking-[0.16em] text-slate-400">
                          {item.manual_review ? "Review" : item.configured ? "Auto" : item.manual ? "Manual" : "Checkout"}
                        </span>
                      </span>
                    </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
                  Online checkout will appear here after a payment API is configured.
                </div>
              )}
              {selectedPlatform?.manual_review ? (
                <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-700">
                  {selectedPlatform.instructions || "Secure checkout opens now. Admin will confirm the payment manually before your wallet updates."}
                </p>
              ) : selectedPlatform?.configured ? (
                <p className="mt-2 text-xs leading-relaxed text-slate-500">You will be sent to a secure hosted checkout. Your wallet updates automatically after the provider confirms payment.</p>
              ) : selectedPlatform?.manual ? (
                <div className="mt-2 text-xs leading-relaxed text-amber-700">
                  <p>
                    {selectedPlatformLabel} is manual. Pay first, upload the screenshot, then admin can approve it from Telegram or the dashboard.
                  </p>
                  {selectedPlatform.account_value ? (
                    <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white/70 px-3 py-2 text-slate-700">
                      <span className="min-w-0">
                        <span className="font-semibold">{selectedPlatform.account_label || "Send to"}:</span>{" "}
                        <span className="font-mono font-bold">{selectedPlatform.account_value}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(selectedPlatform.account_value, `${selectedPlatformLabel} tag`)}
                        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-black text-amber-700 hover:bg-amber-50"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copy
                      </button>
                    </div>
                  ) : null}
                  {selectedPlatform.qr_code_url && (
                    <div className="mt-3 rounded-xl border border-[#2F6FA5]/20 bg-[#EAF4FC] p-3">
                      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#6EA8D8]">Scan QR code</div>
                      <img
                        src={selectedPlatform.qr_code_url}
                        alt={`${selectedPlatformLabel} QR code`}
                        className="mx-auto max-h-56 w-full rounded-lg bg-white object-contain p-2"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-2 text-xs leading-relaxed text-amber-700">Payment API key is not configured for this method yet. Use Chime/Venmo/PayPal manual review or ask admin to enable checkout.</p>
              )}
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-slate-500">Amount ($)</label>
            <input
              type="number"
              required
              min={isWithdraw ? MIN_WITHDRAW_DOLLARS : 1}
              max={isWithdraw ? MAX_DAILY_WITHDRAW_DOLLARS : undefined}
              step={selectedAmountRule?.inputStep || "1"}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              data-testid={isWithdraw ? "withdraw-amount" : "add-money-amount"}
              className={LIGHT_INPUT_ON_DARK_CLASS}
            />
            {selectedAmountRule && (
              <p className="mt-1.5 text-xs leading-snug text-[#2F6FA5]">{selectedAmountRule.message}</p>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {amountPresets.map((v) => (
              <button key={v} type="button" onClick={() => setAmount(String(v))}
                className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-700 hover:bg-slate-50">
                ${v}
              </button>
            ))}
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
              {isWithdraw ? `${selectedPlatformLabel} payout details` : "Note (optional)"}
            </label>
            <input
              type="text"
              required={isWithdraw}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isWithdraw ? `${selectedPlatformLabel} username, phone, or email` : "Payment note"}
              className={LIGHT_INPUT_ON_DARK_CLASS}
            />
            {isWithdraw && (
              <p className="mt-1.5 text-[11px] leading-snug text-slate-500">
                Enter where admin should send your {selectedPlatformLabel} withdrawal. {canAutoPayout ? `$${AUTO_PAYOUT_MIN_DOLLARS} to under $${AUTO_PAYOUT_LIMIT_DOLLARS} can pay automatically when the tag is correct; other valid amounts go to manual review.` : "This method is reviewed manually."} Minimum cashout is ${MIN_WITHDRAW_DOLLARS}.
                {` Maximum wallet withdrawal is $${MAX_DAILY_WITHDRAW_DOLLARS} per 24 hours.`}
                {gameLoadOnlyDollars > 0 ? ` $${gameLoadOnlyDollars.toFixed(2)} must be loaded into a game before cashout.` : ""}
              </p>
            )}
          </div>

          {isWithdraw && (
            <div className="rounded-xl border border-[#2F6FA5]/20 bg-[#EAF4FC] p-2.5">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#6EA8D8]">Want to tip your favorite host?</div>
              <div className="grid grid-cols-3 gap-2">
                {FAVORITE_HOSTS.map((host) => (
                  <button
                    key={host}
                    type="button"
                    onClick={() => setTipHost((current) => (current === host ? "" : host))}
                    className={`rounded-xl border px-2 py-1.5 text-[11px] font-bold transition ${
                      tipHost === host
                        ? "border-[#2F6FA5] bg-[#2F6FA5]/15 text-[#6EA8D8]"
                        : "border-slate-200 bg-white text-slate-700 hover:border-[#2F6FA5]/50"
                    }`}
                  >
                    {host}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {HOST_TIP_AMOUNTS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setTipManual(false);
                      setTipAmount((current) => (current === String(value) ? "" : String(value)));
                    }}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition ${
                      !tipManual && tipAmount === String(value)
                        ? "border-[#2F6FA5] bg-[#2F6FA5]/15 text-[#6EA8D8]"
                        : "border-slate-200 text-slate-600 hover:bg-white"
                    }`}
                  >
                    ${value}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setTipManual(true);
                    setTipAmount("");
                  }}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition ${
                    tipManual
                      ? "border-[#2F6FA5] bg-[#2F6FA5]/15 text-[#6EA8D8]"
                      : "border-slate-200 text-slate-600 hover:bg-white"
                  }`}
                >
                  Manual
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTipHost("");
                    setTipAmount("");
                    setTipManual(false);
                  }}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition ${
                    !tipHost && !tipAmount && !tipManual
                      ? "border-slate-300 bg-white text-slate-700"
                      : "border-slate-200 text-slate-500 hover:bg-white"
                  }`}
                >
                  No tip
                </button>
              </div>
              {tipManual && (
                <div className="mt-3">
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Manual tip amount ($)</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={tipAmount}
                    onChange={(e) => setTipAmount(e.target.value)}
                    placeholder="Enter custom tip"
                    className="w-full rounded-xl border border-white/20 bg-white px-3 py-2 text-xs font-semibold text-slate-950 placeholder:text-slate-400 [caret-color:#0f172a] [color-scheme:light] focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/25"
                  />
                </div>
              )}
              {hasHostTip && (
                <p className="mt-2 text-xs leading-relaxed text-slate-500">
                  Host tip ${tipDollars.toFixed(2)} for {tipHost}. Estimated payout after tip: ${payoutAfterTip.toFixed(2)}.
                </p>
              )}
            </div>
          )}

          {canUploadProof && (
            <div>
              <label className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-slate-500">
                {isWithdraw ? "Payout tag picture (optional)" : "Payment screenshot"}
              </label>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-[#2F6FA5]/35 bg-[#EAF4FC] px-3 py-2.5 text-xs font-bold text-[#2F6FA5] hover:bg-[#dbeefa]">
                <ImagePlus className="h-4 w-4" />
                {proofName || (isWithdraw ? `Upload ${selectedPlatformLabel || "cashout"} tag screenshot` : `Upload ${selectedPlatformLabel || "payment"} screenshot`)}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleProof(e.target.files?.[0])}
                />
              </label>
              {isWithdraw && (
                <p className="mt-1.5 text-[11px] leading-snug text-slate-500">
                  {payoutProofInstruction(selectedPlatform, selectedPlatformLabel)}
                </p>
              )}
              {proofImage && (
                <img src={proofImage} alt={isWithdraw ? "Payout tag proof" : "Payment proof"} className="mt-3 max-h-40 w-full rounded-xl border border-slate-200 object-contain bg-slate-50" />
              )}
            </div>
          )}

          {err && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              <div className="font-bold text-red-800">
                {withdrawLimitError ? "Withdrawal limit reached" : "Request could not be submitted"}
              </div>
              <p className="mt-1 leading-snug">{err}</p>
              {withdrawLimitError && (
                <p className="mt-2 text-xs font-semibold leading-relaxed text-red-700">
                  Wait until older withdrawal requests move outside the 24-hour window, or submit a smaller amount within the remaining limit.
                </p>
              )}
            </div>
          )}

          <div className="pt-1 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:pb-0">
            <button type="submit" disabled={loading}
              className="w-full rounded-full bg-[#2F6FA5] py-3 text-sm font-bold text-white transition-all hover:bg-[#4E91C8] disabled:opacity-50">
              {loading ? "Submitting..." : !isWithdraw && selectedPlatform?.configured ? `Continue to ${selectedPlatformLabel || "checkout"}` : `Submit ${isWithdraw ? `${selectedPlatformLabel} withdrawal` : "add money"} request`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Reel({ emoji }) {
  return (
    <div className="w-16 h-20 rounded-lg border-2 border-[#2F6FA5]/35 bg-[#EAF4FC] grid place-items-center font-display font-black text-3xl text-[#2F6FA5] shadow-sm shrink-0">
      {emoji}
    </div>
  );
}
